import React, { useState, useCallback, useEffect } from 'react';
import { AppBar, Toolbar, Box, IconButton, Drawer, List, ListItem, ListItemText, useMediaQuery, useTheme, Menu, MenuItem } from '@mui/material';
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { colors, gradients } from '../../static/constants/theme';
import { typography } from '../../static/constants/typography';
import Button from '../Utils/components/Button';
import { useSessionStore } from '../../hooks';
import useThemeStore from '../../hooks/useThemeStore';
import { logout } from '../../auth/sessionService';
import { redirectToLogin } from '../../auth/authUtils';
import type { SessionStore } from '../../auth/sessionService';
import getUserDetails from '../../api/userApi';
import { trackEvent } from '../Utils/splunkEventHelper';
 
import logoImage from '../../static/assets/logos/availity-logo.png';
import logoLightImage from '../../static/assets/logos/AvailityLogo-Light.svg';
import mobileLogoImage from '../../static/assets/logos/API-Marketplace-Logo-Footer.svg';
import fileIcon from '../../static/assets/icons/file.svg';
import codeBranchIcon from '../../static/assets/icons/code-branch.svg';
import ticketIcon from '../../static/assets/icons/ticket.svg';
import earlyAccessIcon from '../../static/assets/icons/early-access.svg';
import angleDownIcon from '../../static/assets/icons/angle-down.svg';
import angleRightIcon from '../../static/assets/icons/angle-right.svg';

const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1024));
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [userInfo, setUserInfo] = useState<{ name: string; email: string; initials: string } | null>(null);
  const sessionStore = useSessionStore();
  const { theme: docsTheme } = useThemeStore();
  const { isAuthenticated, setIsAuthenticated, setUser } = useSessionStore((store: unknown) => {
    const sessionStore = store as SessionStore;
    return {
      isAuthenticated: sessionStore.isAuthenticated,
      setIsAuthenticated: sessionStore.setIsAuthenticated,
      setUser: sessionStore.setUser,
    };
  });
  
  const isApiDocsPage = location.pathname.startsWith('/docs') || location.pathname.startsWith('/access-hub');
  const logoToUse = isApiDocsPage && docsTheme === 'light' ? logoLightImage : logoImage;

  const handleMobileMenuToggle = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
  }, []);

  const handleUserMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  }, []);

  const handleUserMenuClose = useCallback(() => {
    setUserMenuAnchor(null);
  }, []);
 
  const handleMobileNavigation = useCallback((path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  }, [navigate]);
  

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const data = await getUserDetails();
        
        if (data) {
          const { firstName, lastName, email } = data;
          const initials = firstName && lastName
            ? `${firstName[0]}${lastName[0]}`
            : firstName?.[0];
          setUser(data);
          setUserInfo({
            name: firstName,
            email,
            initials: initials?.toUpperCase() || ''
          });
        }
      } catch (error) {
        trackEvent({
          event: 'user-fetch-failed',
          status: (error as { response?: { status?: number } })?.response?.status,
          api: 'user-details-api',
        });
      }
    };
    
    if (isAuthenticated) {
      fetchUser();
    } else {
      setUserInfo(null);
    }
  }, [isAuthenticated, setUser]);
  
  const userName = userInfo?.name;
 
  const handleLogout = async () => {
    handleUserMenuClose();
    try {
      await logout(sessionStore as SessionStore);
      navigate('/');
    } catch (error) {
      trackEvent({
        event: 'logout-error',
        status: (error as { response?: { status?: number } })?.response?.status,
        api: 'logout-api',
      });
      setIsAuthenticated(false);
      setUser(null);
      navigate('/');
    }
  };
 
  return (
    <>
      <AppBar position="static" className={`navigation-appbar${isApiDocsPage ? ` navigation-appbar--docs navigation-appbar--docs-${docsTheme}` : ''}`}>
        <Toolbar className="navigation-toolbar">
          <button
            type="button"
            className="logo-button"
            onClick={() => navigate('/')}
            aria-label="Go to homepage"
          >
            <img
              src={isMobile ? mobileLogoImage : logoToUse}
              alt="Availity Developers Logo"
            />
          </button>

          {!isApiDocsPage && (
            <IconButton
              className="nav-mobile-button"
              onClick={handleMobileMenuToggle}
              aria-label="Toggle mobile menu"
              sx={{ marginLeft: '24px' }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box className="nav-desktop">
            {!isApiDocsPage && (
              <>
                <Button variant="navbar-login" onClick={() => navigate('/docs')}>
                  API Documentation
                </Button>
                <Button variant="navbar-login" onClick={() => navigate('/docs/ai-management')}>Model Context Protocol (MCP)</Button>
              </>
            )}
          </Box>
          
          {/* Desktop Auth Buttons */}
          <Box className="nav-desktop-auth">
            {isAuthenticated ? (
              <>
                <Box 
                  onClick={handleUserMenuOpen}
                  sx={{ 
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: '16px',
                    height: '40px',
                    borderRadius: '100px',
                    cursor: 'pointer'
                  }}
                >
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '40px',
                    height: '40px',
                    borderRadius: '100px',
                    background: gradients.avatarBorder,
                    padding: '2px'
                  }}>
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      width: '100%',
                      height: '100%',
                      borderRadius: '100px',
                      background: isApiDocsPage && docsTheme === 'light' ? colors.white : colors.primary,
                      color: isApiDocsPage && docsTheme === 'light' ? colors.text.primary : colors.white,
                      ...typography.montSemibold16
                    }}>
                      {userInfo?.initials}
                    </Box>
                  </Box>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}>
                    <Box sx={{
                      color: isApiDocsPage && docsTheme === 'light' ? colors.text.primary : colors.white,
                      letterSpacing: '0.01em',
                      ...typography.latoBold16
                    }}>
                      {userName}
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img 
                        src={userMenuAnchor ? angleRightIcon : angleDownIcon} 
                        alt="" 
                        style={{ 
                          width: '12px', 
                          height: '12px', 
                          filter: isApiDocsPage && docsTheme === 'light' ? 'brightness(0) saturate(100%) invert(13%) sepia(28%) saturate(2487%) hue-rotate(188deg) brightness(95%) contrast(96%)' : 'brightness(0) invert(1)' 
                        }} 
                      />
                    </Box>
                  </Box>
                </Box>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  sx={{
                    '& .MuiPaper-root': {
                      background: colors.white,
                      borderRadius: '8px',
                      marginTop: '8px',
                      minWidth: '160px',
                      border: `1px solid ${colors.border.lighter}`
                    }
                  }}
                >
                  <MenuItem 
                    onClick={handleLogout}
                    disableRipple
                    sx={{
                      color: colors.text.primary,
                      padding: '12px 20px',
                      background: 'transparent !important',
                      ...typography.latoMedium14,
                      '&:hover': {
                        background: 'transparent !important',
                        color: colors.accent.orange
                      }
                    }}
                  >
                    Logout
                  </MenuItem>
                </Menu>
              </>
            ) : (
              isAuthenticated === false && (
                <>
                  <Button variant="navbar-login" onClick={redirectToLogin}>Login</Button>
                  <Button variant="navbar-signup" onClick={() => navigate('/join-early')}>
                    <img src={earlyAccessIcon} alt="" style={{ width: 16, height: 16, marginRight: 6 }} />
                    Join Early
                  </Button>
                </>
              )
            )}
          </Box>

          {/* Mobile/Tablet Auth Buttons */}
          <Box className="nav-mobile-auth">
            {isAuthenticated ? (
              <Box 
                onClick={handleUserMenuOpen}
                sx={{ 
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '8px',
                  height: '40px',
                  borderRadius: '100px',
                  cursor: 'pointer'
                }}
              >
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: '100px',
                  background: gradients.avatarBorder,
                  padding: '2px'
                }}>
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    borderRadius: '100px',
                    background: isApiDocsPage && docsTheme === 'light' ? colors.white : colors.primary,
                    color: isApiDocsPage && docsTheme === 'light' ? colors.text.primary : colors.white,
                    ...typography.montSemibold16
                  }}>
                    {userInfo?.initials}
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <Box sx={{
                    color: isApiDocsPage && docsTheme === 'light' ? colors.text.primary : colors.white,
                    letterSpacing: '0.01em',
                    ...typography.latoBold16
                  }}>
                    {userName}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img 
                      src={userMenuAnchor ? angleRightIcon : angleDownIcon} 
                      alt="" 
                      style={{ 
                        width: '12px', 
                        height: '12px', 
                        filter: isApiDocsPage && docsTheme === 'light' ? 'brightness(0) saturate(100%) invert(13%) sepia(28%) saturate(2487%) hue-rotate(188deg) brightness(95%) contrast(96%)' : 'brightness(0) invert(1)' 
                      }} 
                    />
                  </Box>
                </Box>
              </Box>
            ) : (
              isAuthenticated === false && (
                <>
                  <Button variant="navbar-login" onClick={redirectToLogin}>Login</Button>
                  <Button variant="navbar-signup" onClick={() => navigate('/join-early')}>
                    <img src={earlyAccessIcon} alt="" style={{ width: 16, height: 16, marginRight: 6 }} />
                    Join Early
                  </Button>
                </>
              )
            )}
          </Box>
 
        </Toolbar>
      </AppBar>
 
      <Drawer
        anchor="left"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        className="mobile-drawer"
      >
        <Box className="drawer-header">
          <IconButton onClick={handleMobileMenuToggle} className="drawer-close-button" aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        </Box>
        <List>
          <ListItem component="button" onClick={() => handleMobileNavigation('/docs')}>
            <img src={fileIcon} alt="" className="drawer-icon" />
            <ListItemText primary="API Documentation" />
          </ListItem>
          {!isApiDocsPage && (
            <ListItem component="button" onClick={() => handleMobileNavigation('/docs/ai-management')}>
              <img src={codeBranchIcon} alt="" className="drawer-icon" />
              <ListItemText primary="Model Context Protocol (MCP)" />
            </ListItem>
          )}
          {!isAuthenticated && (
            <ListItem component="button" onClick={() => handleMobileNavigation('/join-early')} className="list-item-signup">
              <img src={ticketIcon} alt="" className="drawer-icon" />
              <ListItemText primary="Join Early" />
            </ListItem>
          )}
        </List>
      </Drawer>
    </>
  );
};
 
export default Navigation;